import React from 'react';
import { motion } from 'framer-motion';
import { FlaskConicalIcon, MegaphoneIcon, MonitorIcon, UserRoundIcon, UsersIcon, VideoIcon } from 'lucide-react';
import { snakificationCandidates } from '../../data/snakificationCandidates';
import { InteriorSlide, SlideBanner } from './SlideShell';
import type { SlideProps } from './SlideShell';
const ICONS = {
  video: VideoIcon,
  monitor: MonitorIcon,
  megaphone: MegaphoneIcon,
  users: UsersIcon,
  flask: FlaskConicalIcon,
  patient: UserRoundIcon
} as const;
export function SlideWhatToSnakify({}: SlideProps) {
  return <InteriorSlide banner={<SlideBanner data-id="element-180" data-mp="t65i6rkegj0v">
          Snakification is not about the asset type. It is about making
          important information easier to notice, understand and act on.
        </SlideBanner>} data-id="element-179" data-mp="t76wd4wpplmt">
      <motion.header initial={{
      opacity: 0,
      y: 18
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="flex flex-shrink-0 flex-col gap-[clamp(0.5rem,1vw,0.9rem)] lg:flex-row lg:items-end lg:justify-between lg:gap-10" data-id="element-181" data-mp="3dyh1uwgm0683">
        <div className="min-w-0" data-id="element-182" data-mp="1w7b8w5zzw57r">
          <p className="mb-2 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-183" data-mp="1w68hlwyrg0rb">
            The principle
          </p>
          <h2 className="max-w-3xl text-[clamp(1.7rem,3.2vw,2.9rem)] font-extrabold uppercase leading-[1.02] tracking-[-0.04em] text-[var(--fg)]" data-id="element-184" data-mp="1n5k1mwpt3qbs">
            If it matters,{' '}
            <span className="text-[var(--brand)]" data-id="element-185" data-mp="1d7xb1cz5aabz">
              make it easier to engage with.
            </span>
          </h2>
        </div>
        <p className="max-w-sm flex-shrink-0 text-[clamp(0.82rem,1.1vw,1rem)] font-semibold leading-snug text-[var(--muted)] lg:text-right" data-id="element-186" data-mp="zi29v16jks8t">
          Anything important enough to earn HCP attention and understanding can
          be Snakified.
        </p>
      </motion.header>

      <p className="mt-[clamp(0.7rem,1.4vw,1.2rem)] flex-shrink-0 text-[clamp(0.58rem,0.8vw,0.72rem)] font-bold uppercase tracking-[0.2em] text-[var(--muted)]" data-id="element-187" data-mp="uhxrf5m2pnfy">
        For example
      </p>

      <div className="mt-[clamp(0.5rem,0.9vw,0.8rem)] grid min-h-0 flex-1 grid-cols-1 gap-[clamp(0.5rem,1vw,0.95rem)] sm:grid-cols-2 lg:grid-cols-3" data-id="element-188" data-mp="6m2ptxnaxr1e">
        {snakificationCandidates.map((candidate, index) => {
        const Icon = ICONS[candidate.icon];
        return <motion.article key={candidate.label} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.16 + index * 0.07,
          duration: 0.45
        }} className="flex min-h-0 flex-col rounded-2xl border border-[var(--fg)]/10 bg-[var(--surface)] p-[clamp(0.75rem,1.3vw,1.25rem)]" data-id="element-189" data-mp="ubnb4n6xh0du" data-mpi={index}>
              <div className="flex min-h-0 flex-1 items-center gap-[clamp(0.5rem,1vw,0.9rem)]" data-id="element-190" data-mp="fh0ozmh1m016">
                <span className="flex h-[clamp(2rem,2.6vw,2.6rem)] w-[clamp(2rem,2.6vw,2.6rem)] flex-shrink-0 items-center justify-center rounded-xl bg-[var(--brand)]" data-id="element-191" data-mp="351lv2xf5s55x">
                  <Icon className="h-[clamp(1rem,1.3vw,1.25rem)] w-[clamp(1rem,1.3vw,1.25rem)] text-white" aria-hidden="true" data-id="element-192" data-mp="1fofefp5nqzjq" />
                </span>
                <div className="flex min-h-0 flex-1 items-center justify-end self-stretch overflow-hidden rounded-xl bg-white p-1 ring-1 ring-[var(--fg)]/10" data-id="element-193" data-mp="1pjpkcnz6fr2s">
                  <img src={candidate.image} alt={candidate.alt} className="h-full min-h-0 w-full rounded-lg object-cover" data-id="element-194" data-mp="1zblly90h1zbr" />
                </div>
              </div>

              <h3 className="mt-[clamp(0.55rem,1vw,0.9rem)] flex-shrink-0 text-[clamp(0.66rem,0.9vw,0.82rem)] font-extrabold uppercase tracking-[0.18em] text-[var(--brand)]" data-id="element-195" data-mp="289fct4fgw0rj">
                {candidate.label}
              </h3>
              <p className="mt-[clamp(0.25rem,0.5vw,0.45rem)] flex-shrink-0 text-[clamp(0.85rem,1.2vw,1.15rem)] font-semibold leading-snug tracking-[-0.02em] text-[var(--fg)]" data-id="element-196" data-mp="iwbvctx3s3rw">
                {candidate.detail}
              </p>
            </motion.article>;
      })}
      </div>
    </InteriorSlide>;
}